import { ComponentProps } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { type VariantProps } from 'class-variance-authority';
import {
  CircleCheck,
  CircleHelp,
  CircleX,
  Info,
  Trash2,
  TriangleAlert,
  XIcon,
} from 'lucide-react';

import { buttonVariants } from '@/components/ui/button';
import {
  DIALOG_CLOSE_CLASS_NAME,
  DIALOG_CONTENT_CLASS_NAME,
  DIALOG_DESCRIPTION_CLASS_NAME,
  DIALOG_FOOTER_CLASS_NAME,
  DIALOG_HEADER_CLASS_NAME,
} from '@/components/ui/dialog';

import { cn } from '@/lib/utils';

function AlertDialog({
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return (
    <AlertDialogPrimitive.Root
      data-slot="alert-dialog"
      {...props}
    />
  );
}

function AlertDialogTrigger({
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger
      data-slot="alert-dialog-trigger"
      {...props}
    />
  );
}

function AlertDialogPortal({
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal
      data-slot="alert-dialog-portal"
      {...props}
    />
  );
}

function AlertDialogOverlay({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-overlay fixed inset-0 z-50',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(DIALOG_CONTENT_CLASS_NAME, className)}
        {...props}
      >
        {children}
        {showCloseButton && (
          <AlertDialogPrimitive.Cancel
            data-slot="alert-dialog-close"
            className={cn(
              DIALOG_CLOSE_CLASS_NAME,
              'absolute top-3 right-3 flex cursor-pointer items-center justify-center transition-colors [&_svg]:size-4',
            )}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </AlertDialogPrimitive.Cancel>
        )}
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
}

type AlertDialogTone =
  | 'warning'
  | 'destructive'
  | 'info'
  | 'confirm'
  | 'success'
  | 'error';

const TONE_ICONS = {
  warning: TriangleAlert,
  destructive: Trash2,
  info: Info,
  confirm: CircleHelp,
  success: CircleCheck,
  error: CircleX,
};

const TONE_CLASSES: Record<AlertDialogTone, string> = {
  warning: 'bg-warning-soft text-warning-soft-foreground',
  destructive: 'bg-destructive-soft text-destructive-soft-foreground',
  info: 'bg-info-soft text-info-soft-foreground',
  confirm: 'bg-info-soft text-info-soft-foreground',
  success: 'bg-success-soft text-success-soft-foreground',
  error: 'bg-destructive-soft text-destructive-soft-foreground',
};

function AlertDialogHeader({
  className,
  tone,
  children,
  ...props
}: ComponentProps<'div'> & { tone?: AlertDialogTone }) {
  if (!tone) {
    return (
      <div
        data-slot="alert-dialog-header"
        className={cn(DIALOG_HEADER_CLASS_NAME, className)}
        {...props}
      >
        {children}
      </div>
    );
  }

  const Icon = TONE_ICONS[tone];

  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex flex-row items-start gap-3 text-left', className)}
      {...props}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          TONE_CLASSES[tone],
        )}
      >
        <Icon className="size-4.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
    </div>
  );
}

function AlertDialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(DIALOG_FOOTER_CLASS_NAME, className)}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        'pr-8 text-lg leading-7 font-semibold tracking-tight',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(DIALOG_DESCRIPTION_CLASS_NAME, className)}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  variant,
  size,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action> &
  VariantProps<typeof buttonVariants>) {
  return (
    <AlertDialogPrimitive.Action
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function AlertDialogCancel({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: 'outline' }), className)}
      {...props}
    />
  );
}

export type { AlertDialogTone };

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
